import FileCopy from '@mui/icons-material/FileCopy'
import { IconButton } from '@mui/material'
import { useTabContext } from 'hooks/useTabContext'
import { useMemo, ReactNode } from 'react'

import Tab, { TabProps } from './Tab'
import Tabs from './Tabs'

interface Props {
  children: ReactNode
  labels: string[]
  panelClassName?: string
  tabProps?: TabProps
  keepAlive?: boolean
  handleCopy?: (tabIdx: number) => void
}

function UncontrolledTabs({
  labels,
  panelClassName,
  children,
  tabProps = {},
  keepAlive,
  handleCopy,
}: Props) {
  const { currentTabIndex, handleTabChange } = useTabContext()

  const panels = useMemo(() => {
    const nodes = Array.isArray(children) ? children : [children]

    if (keepAlive) {
      return nodes.map((node, idx) => (
        <div
          key={`${labels[idx]}-panel`}
          style={idx !== currentTabIndex ? { display: 'none' } : undefined}
          className={panelClassName}
        >
          {node}
        </div>
      ))
    }

    return <div className={panelClassName}>{nodes[currentTabIndex]}</div>
  }, [children, labels, keepAlive, panelClassName, currentTabIndex])

  return (
    <>
      {handleCopy && (
        <div style={{ position: 'absolute', right: 2, top: 4, zIndex: 10 }}>
          <IconButton color="secondary" onClick={() => handleCopy(currentTabIndex)}>
            <FileCopy />
          </IconButton>
        </div>
      )}
      <Tabs
        value={currentTabIndex}
        onChange={handleTabChange}
        indicatorColor="primary"
        textColor="primary"
        data-testid="document-detail-tab"
      >
        {labels.map((label) => (
          <Tab {...tabProps} key={label} label={label} />
        ))}
      </Tabs>

      {panels}
    </>
  )
}

export default UncontrolledTabs
